// Rules about apply-time questions that both the popup and the deck slice have to
// agree on. Kept in one place because the two disagreeing is how a question either
// gets asked twice or, worse, gets dropped from an application that needed it.
//
// The source of truth is flash-collection's `applicationQuestions.ts` /
// `mapUserDataToFields.ts`; these mirror its contract for the client.

/** The backend only banks an answer on the profile when it stamped `savedToProfile`. */
export const isProfileBacked = field => field?.savedToProfile === true;

/** Older decks predate `blocking`; `required` is what they carried instead. */
export const isBlocking = field => field?.blocking ?? Boolean(field?.required);

/**
 * The application-brief "vouch" question ("Have you used Terraform?" / "...
 * Kubernetes?" / ...) is one classification, `vouchSkill`, shared by every skill —
 * the skill lives in the label, not the classification. `flash-collection` fixes
 * this on purpose (see `NEXT-STAGE-PLAN-2026-09-04.md` essential 2); it means the
 * classification alone can no longer identify a question.
 */
export const VOUCH_SKILL_CLASSIFICATION = 'vouchSkill';
export const isVouchSkillField = field => field?.classification === VOUCH_SKILL_CLASSIFICATION;

/**
 * The subject a question is about, when its classification cannot say. The gaps interview
 * (NEXT_STAGE_INTAKE) puts three "How many years have you used X?" questions in one sheet and every
 * one of them is `skillYears`; two story prompts are both `applicantStory`. The server names the
 * subject in `meta` for exactly this reason, and it is what keeps them apart here.
 */
const metaSubject = field => field?.meta?.skill ?? field?.meta?.promptKey ?? null;

/**
 * A question's identity for dedup, banking and dismissal. For a plain question this is exactly
 * `classification || label`, unchanged from before grouped questions existed.
 *
 * Two kinds of question need more than their classification, for the same reason: several of them
 * ride one card sharing a single classification string. `vouchSkill` carries its skill in the label,
 * so the label is folded in — otherwise confirming (or dismissing) "Have you used Terraform?" would
 * silently resolve "Have you used Kubernetes?" too, on the same card and for the rest of the
 * session. Gaps questions carry theirs in `meta`, so the meta subject is folded in instead: the
 * label is the server's sentence and can be reworded, but `meta.skill` is the thing itself.
 *
 * Both are normalised (trimmed, lower-cased) before folding in, so the same subject arriving with
 * different casing across two cached decks still dedupes to one identity instead of asking twice.
 */
export const questionIdentity = field => {
    if (!field) return undefined;
    if (isVouchSkillField(field)) {
        return `${VOUCH_SKILL_CLASSIFICATION}:${(field.label ?? '').trim().toLowerCase()}`;
    }
    const subject = metaSubject(field);
    if (subject) return `${field.classification || field.label}:${String(subject).trim().toLowerCase()}`;
    return field.classification || field.label;
};

/**
 * The two documents the profile stores, keyed by classification. Mirrors
 * flash-collection's `FILE_ANSWER_CATEGORY`. Needed client-side as well because
 * decks cached before uploads had an `answerType` carry "text" — which is what
 * drew a keyboard over a transcript upload. The classification is never wrong.
 */
export const FILE_ANSWER_CATEGORY = {
    resume: 'resume',
    resumeAttachButton: 'resume',
    'educationHistory.transcript': 'transcript',
};

/**
 * Classifications whose only answer is Yes or No. A copy of flash-collection's
 * `YES_NO_CLASSIFICATIONS` (feat/pre-prod-reliability); no package export reaches this repo.
 * Needed client-side because a deck cached before the server stamped these `boolean`
 * carries no answerType — the free-text box is how "yuh" reached the answer bank and was
 * replayed on every later form. Deliberately NOT here, same as the package: questions with a
 * real third answer ("Prefer not to answer" on convictions, disciplinary action, firings, first
 * generation) and ones that take free text on real forms (`hasLicenses` "list your licenses",
 * `willingToTravel` "what percentage", `consideringOtherOffers`).
 */
export const YES_NO_CLASSIFICATIONS = new Set([
    'authorizedToWork', 'authorizedToWorkInUS', 'authorizedToWorkInCA', 'authorizedToWorkInUK', 'requireSponsorship',
    'isUsCitizen', 'isInUs', 'willingToRelocate', 'willingToCommute', 'willingToWorkOnsite',
    'willingToWorkRemote', 'willingToWorkHybrid', 'willingToWorkOvertime', 'willingToWorkShift',
    'willlingToHaveBackgroundCheck', 'hasDriversLicenseAndVehicle', 'hasNoncompete', 'isGovernmentEmployee',
    'wasGovernmentEmployeeInPast',
]);

/** Where a `file` answer is stored; null means we have nowhere to put it. */
export const fileCategoryFor = field =>
    field?.fileCategory ?? FILE_ANSWER_CATEGORY[field?.classification] ?? null;

/**
 * Interchangeable "show your work" links: the fill layer satisfies any one of these
 * from any other (`CLASSIFICATION_PROFILE_FALLBACKS`), so once the user gives us one
 * the backend stops asking for its siblings. LinkedIn is deliberately absent — it
 * needs a linkedin.com URL and borrows from nothing.
 */
export const INTERCHANGEABLE_LINKS = ['website', 'portfolio', 'github'];

/**
 * A link answer is written straight to the profile and replayed on every future
 * application, and nothing downstream checks its shape — flash-collection stores
 * whatever non-empty string arrives. A half-typed "linkedin.com/in/jak" would
 * therefore stick forever and fail every URL-validating ATS (Lever, Ashby) from then
 * on, silently. So normalise here: add the scheme people leave off, and reject
 * anything that still isn't a hostname. Returns null when it can't be salvaged.
 */
export const normalizeUrlAnswer = (raw) => {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) return null;
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    let url;
    try {
        url = new URL(withScheme);
    } catch {
        return null;
    }
    if (!/^https?:$/.test(url.protocol)) return null;
    // "https://linkedin" parses fine and is a useless profile link.
    if (!/^[^\s.]+(\.[^\s.]+)+$/.test(url.hostname)) return null;
    return url.toString();
};
